import React, { useState, useEffect } from "react";
import { collection, getDocs, doc } from "firebase/firestore";
import { db } from "./firebase";
import DancerThumbnail from "./DancerThumbnail";
import "./AdminStars.css";

// ── Discipline display order & colours ───────────────────────────────────────
const DISCIPLINE_ORDER = ["Latin", "Contemporary", "Hip Hop", "Commercial", "Show Dance"];
const DISCIPLINE_COLORS = {
  "Latin":        { bg: "#fff3e0", border: "#f57c00", icon: "💃" },
  "Contemporary": { bg: "#e8f5e9", border: "#388e3c", icon: "🎭" },
  "Hip Hop":      { bg: "#fce4ec", border: "#c2185b", icon: "🎤" },
  "Commercial":   { bg: "#e3f2fd", border: "#1976d2", icon: "🎬" },
  "Show Dance":   { bg: "#f3e5f5", border: "#7b1fa2", icon: "🎪" },
};

const LATIN_DANCE_ORDER = ["Samba", "ChaChaCha", "Rumba", "Pasodoble", "Jive"];

// Age categories ordered from oldest to youngest
const AGE_CATEGORY_ORDER = ["Adult", "Youth", "Junior II", "Junior I", "Juvenile II", "Juvenile I"];

// Extract age category from a full category string like "Solo Adult" or "Duo Junior II"
function extractAgeCategory(categoryStr) {
  for (const age of AGE_CATEGORY_ORDER) {
    if (categoryStr.includes(age)) return age;
  }
  return null;
}

const RANK_STYLES = [
  { label: "1st", bg: "#FFD700", color: "#7a5a00", icon: "🥇" },
  { label: "2nd", bg: "#E8E8E8", color: "#444",    icon: "🥈" },
  { label: "3rd", bg: "#cd7f32", color: "#fff",    icon: "🥉" },
];

export default function AdminStars() {
  const [categories, setCategories]   = useState([]); // all loaded data
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [search, setSearch]           = useState("");
  const [filterDisc, setFilterDisc]   = useState("All");
  const [filterAge, setFilterAge]     = useState("All");
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);

        // Load all category docs
        const snap = await getDocs(collection(db, "globalRankingsCache"));

        const cats = [];
        snap.forEach(d => {
          if (d.id === "_metadata") {
            const meta = d.data();
            if (meta.lastCalculated?.toDate) setLastUpdated(meta.lastCalculated.toDate());
            return;
          }
          const data = d.data();
          if (!data.rankings || data.rankings.length === 0) return;

          const top3 = data.rankings
            .filter(r => r.currentRank <= 3)
            .sort((a, b) => a.currentRank - b.currentRank)
            .slice(0, 3)
            .map(r => ({
              ...r,
              // Most recent competition's studio = current studio
              currentStudio: r.results?.[0]?.studio || null,
            }));

          if (top3.length === 0) return;

          const catStr = data.category || d.id;
          cats.push({
            id: d.id,
            discipline:   data.discipline || "Unknown",
            category:     catStr,
            ageCategory:  extractAgeCategory(catStr),
            dance:         data.dance      || null,
            totalDancers: data.totalDancers || data.rankings.length,
            top3,
          });
        });

        // Sort: discipline order → age category order → Latin dance order
        cats.sort((a, b) => {
          const di = DISCIPLINE_ORDER.indexOf(a.discipline) - DISCIPLINE_ORDER.indexOf(b.discipline);
          if (di !== 0) return di;
          // Sort by age category using AGE_CATEGORY_ORDER
          const aiAge = AGE_CATEGORY_ORDER.indexOf(a.ageCategory);
          const biAge = AGE_CATEGORY_ORDER.indexOf(b.ageCategory);
          const ageI = (aiAge === -1 ? 999 : aiAge) - (biAge === -1 ? 999 : biAge);
          if (ageI !== 0) return ageI;
          // Latin dances: fixed order Samba, ChaChaCha, Rumba, Pasodoble, Jive
          if (a.discipline === "Latin" && a.dance && b.dance) {
            const ai = LATIN_DANCE_ORDER.indexOf(a.dance);
            const bi = LATIN_DANCE_ORDER.indexOf(b.dance);
            if (ai !== -1 || bi !== -1) {
              return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
            }
          }
          return (a.dance || "").localeCompare(b.dance || "");
        });

        setCategories(cats);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // ── Filtering ────────────────────────────────────────────────────────────
  const disciplines = ["All", ...DISCIPLINE_ORDER.filter(d =>
    categories.some(c => c.discipline === d)
  )];

  const visible = categories.filter(cat => {
    if (filterDisc !== "All" && cat.discipline !== filterDisc) return false;
    if (filterAge !== "All" && cat.ageCategory !== filterAge) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      cat.category.toLowerCase().includes(q) ||
      cat.discipline.toLowerCase().includes(q) ||
      (cat.dance || "").toLowerCase().includes(q) ||
      cat.top3.some(r => r.dancerName.toLowerCase().includes(q))
    );
  });

  // Group by discipline for display
  const grouped = {};
  visible.forEach(cat => {
    if (!grouped[cat.discipline]) grouped[cat.discipline] = [];
    grouped[cat.discipline].push(cat);
  });

  // ── Render ───────────────────────────────────────────────────────────────
  if (loading) return (
    <div className="admin-stars__loading">
      <div className="admin-stars__spinner" />
      <p>Loading star rankings…</p>
    </div>
  );

  if (error) return (
    <div className="admin-stars__error">⚠️ Error loading data: {error}</div>
  );

  return (
    <div className="admin-stars">
      {/* Header */}
      <div className="admin-stars__header">
        <div>
          <h1>⭐ Current Stars</h1>
          <p className="admin-stars__subtitle">
            Top 3 ranked dancers per discipline / category / dance
            {lastUpdated && (
              <span className="admin-stars__updated">
                &nbsp;· Last updated: {lastUpdated.toLocaleString("el-GR")}
              </span>
            )}
          </p>
        </div>
        <div className="admin-stars__stats">
          <span className="admin-stars__stat-pill">{categories.length} categories</span>
          <span className="admin-stars__stat-pill">{visible.length} shown</span>
        </div>
      </div>

      {/* Controls */}
      <div className="admin-stars__controls">
        <input
          className="admin-stars__search"
          type="text"
          placeholder="Search category, discipline, dance or dancer…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <div className="admin-stars__disc-filters">
          {disciplines.map(d => (
            <button
              key={d}
              className={`admin-stars__disc-btn ${filterDisc === d ? "active" : ""}`}
              onClick={() => setFilterDisc(d)}
              style={d !== "All" && filterDisc === d ? {
                background: DISCIPLINE_COLORS[d]?.border,
                borderColor: DISCIPLINE_COLORS[d]?.border,
                color: "#fff",
              } : {}}
            >
              {d}
            </button>
          ))}
        </div>
        <div className="admin-stars__age-filters">
          {["All", ...AGE_CATEGORY_ORDER].map(age => (
            <button
              key={age}
              className={`admin-stars__age-btn ${filterAge === age ? "active" : ""}`}
              onClick={() => setFilterAge(age)}
            >
              {age}
            </button>
          ))}
        </div>
      </div>

      {visible.length === 0 && (
        <div className="admin-stars__empty">No categories match your search.</div>
      )}

      {/* Grouped sections */}
      {DISCIPLINE_ORDER.filter(d => grouped[d]).map(disc => {
        const color = DISCIPLINE_COLORS[disc] || { bg: "#f5f5f5", border: "#999", icon: "🎵" };
        return (
          <section key={disc} className="admin-stars__discipline-section">
            <h2
              className="admin-stars__discipline-heading"
              style={{ borderColor: color.border, color: color.border }}
            >
              {disc}
              <span className="admin-stars__cat-count">{grouped[disc].length} categories</span>
            </h2>

            {disc === "Latin" ? (
              // Latin: group by age category, each age group on its own full-width row
              (() => {
                const byAge = {};
                grouped[disc].forEach(cat => {
                  const age = cat.ageCategory || "Other";
                  if (!byAge[age]) byAge[age] = [];
                  byAge[age].push(cat);
                });
                const ageKeys = [...AGE_CATEGORY_ORDER, "Other"].filter(a => byAge[a]);
                return ageKeys.map(age => (
                  <div key={age} className="admin-stars__age-row">
                    <div className="admin-stars__age-row-label">{age}</div>
                    <div className="admin-stars__grid admin-stars__grid--latin-row">
                      {byAge[age].map(cat => (
                        <CategoryCard key={cat.id} cat={cat} color={color} />
                      ))}
                    </div>
                  </div>
                ));
              })()
            ) : (
              <div className="admin-stars__grid">
                {grouped[disc].map(cat => (
                  <CategoryCard key={cat.id} cat={cat} color={color} />
                ))}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}

// ── CategoryCard ─────────────────────────────────────────────────────────────
function CategoryCard({ cat, color }) {
  // Build a readable label: strip "Solo " prefix and discipline prefix from category
  const label = cat.dance
    ? cat.dance
    : cat.category
        .replace(/^Solo\s+/i, "")
        .replace(new RegExp(`^${cat.discipline}\\s*`, "i"), "")
        .trim() || cat.category;

  return (
    <div
      className="admin-stars__card"
      style={{ borderColor: color.border, background: color.bg }}
    >
      <div className="admin-stars__card-header" style={{ borderBottomColor: color.border }}>
        <span className="admin-stars__card-label">{label}</span>
        <span className="admin-stars__card-total" title="Total dancers ranked">
          {cat.totalDancers} dancers
        </span>
      </div>

      <div className="admin-stars__podium">
        {[0, 1, 2].map(i => {
          const dancer = cat.top3[i];
          const rs = RANK_STYLES[i];
          return (
            <div
              key={i}
              className={`admin-stars__podium-slot admin-stars__podium-slot--${i + 1}`}
            >
              {dancer ? (
                <>
                  <div
                    className="admin-stars__rank-badge"
                    style={{ background: rs.bg, color: rs.color }}
                  >
                    {rs.icon}
                  </div>
                  <DancerThumbnail
                    dancerName={dancer.dancerName}
                    size={44}
                    className="admin-stars__thumb"
                  />
                  <div className="admin-stars__dancer-info">
                    <a
                      href={`/dancer/${encodeURIComponent(dancer.dancerName)}`}
                      className="admin-stars__dancer-name"
                      title={dancer.dancerName}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {dancer.dancerName}
                    </a>
                    {dancer.currentStudio && (
                      <span className="admin-stars__studio" title={dancer.currentStudio}>
                        {dancer.currentStudio}
                      </span>
                    )}
                    <div className="admin-stars__meta">
                      <span className="admin-stars__points">{dancer.totalPoints.toLocaleString()} pts</span>
                      {dancer.currentDaysHeld > 0 && (
                        <span className="admin-stars__days">⏱ {dancer.currentDaysHeld}d</span>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <div className="admin-stars__empty-slot">
                  <span style={{ fontSize: "1.4rem", opacity: 0.3 }}>{rs.icon}</span>
                  <span className="admin-stars__empty-text">—</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
